/* ============================================================
   sk-reviews.js — render REAL Judge.me reviews into the SK
   review-card design (.sk-meal-reviews .rev-list > .review).

   Why: Judge.me hands the theme only the aggregate score/count
   (product.metafields.reviews.*), NOT the review bodies — those
   are loaded client-side by Judge.me's own widget. So we let the
   Judge.me widget (section sk-judgeme-widget) load as the DATA
   SOURCE, read the real reviews out of it, and re-render them in
   our own card markup. The Judge.me widget's summary/histogram/
   list are then hidden by CSS (scoped to html.sk-reviews-active),
   while its native "Write a review" button + form stay live.

   Defensive: we only add html.sk-reviews-active AFTER at least one
   real card renders — so if Judge.me never loads (JS blocked), the
   reskinned Judge.me widget shows as a graceful fallback.
   ============================================================ */
(function () {
  'use strict';

  // Loaded by BOTH sk-meal-reviews and sk-judgeme-widget (so the widget self-manages even on a
  // template that has the widget but no reviews section, e.g. a lean PDP) — run once.
  if (window.__skReviewsInit) return;
  window.__skReviewsInit = true;

  var SRC  = '.sk-judgeme-widget';      // Judge.me widget = data source
  var LIST = '[data-sk-reviews-list]';  // our target container in sk-meal-reviews
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function initials(name) {
    if (!name) return '';
    var parts = name.trim().split(/\s+/);
    var a = parts[0] ? parts[0].charAt(0) : '';
    var b = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (a + b).toUpperCase();
  }

  function fmtDate(rev) {
    var ts = rev.querySelector('.jdgm-rev__timestamp');
    var raw = (ts && (ts.getAttribute('data-content') || ts.textContent)) || '';
    var m = raw.match(/(\d{4})-(\d{2})-(\d{2})/); // data-content: "2026-06-22 09:27:00 UTC"
    if (m) return parseInt(m[3], 10) + ' ' + MONTHS[parseInt(m[2], 10) - 1] + ' ' + m[1];
    return ts ? ts.textContent.trim() : '';
  }

  function score(rev) {
    var ratingEl = rev.querySelector('.jdgm-rev__rating');
    var s = parseInt((ratingEl && ratingEl.getAttribute('data-score')) || rev.getAttribute('data-score') || '5', 10);
    return Math.max(0, Math.min(5, isNaN(s) ? 5 : s));
  }

  function buildCard(rev) {
    var s = score(rev);
    var authorEl = rev.querySelector('.jdgm-rev__author');
    var bodyEl = rev.querySelector('.jdgm-rev__body');
    var titleEl = rev.querySelector('.jdgm-rev__title');
    var name = authorEl ? authorEl.textContent.trim() : '';
    // Prefer the body; fall back to the title if a review has no body text.
    var body = (bodyEl && bodyEl.textContent.trim()) || (titleEl && titleEl.textContent.trim()) || '';
    var verified = rev.getAttribute('data-verified-buyer') === 'true';

    var card = document.createElement('div');
    card.className = 'review';

    var st = document.createElement('span');
    st.className = 'st';
    st.setAttribute('aria-label', s + ' out of 5 stars');
    st.textContent = new Array(s + 1).join('★') + new Array(6 - s).join('☆');
    card.appendChild(st);

    if (body) {
      var rq = document.createElement('p');
      rq.className = 'rq';
      rq.textContent = body; // textContent — review text is user content, never innerHTML
      card.appendChild(rq);
    }

    var rby = document.createElement('div');
    rby.className = 'rby';

    var av = document.createElement('span');
    av.className = 'av';
    av.setAttribute('aria-hidden', 'true');
    av.textContent = initials(name);
    rby.appendChild(av);

    var meta = document.createElement('div');
    var rn = document.createElement('div');
    rn.className = 'rn';
    rn.textContent = name;
    meta.appendChild(rn);
    var date = fmtDate(rev);
    if (date) {
      var rr = document.createElement('div');
      rr.className = 'rr';
      rr.textContent = date;
      meta.appendChild(rr);
    }
    rby.appendChild(meta);

    if (verified) {
      var vp = document.createElement('span');
      vp.className = 'vp';
      vp.textContent = '✓ Verified';
      rby.appendChild(vp);
    }

    card.appendChild(rby);
    return card;
  }

  // True once Judge.me has finished setting up its widget (so a genuinely-0-review product is
  // distinguishable from one that's just still loading).
  function widgetReady(src) {
    return !!(src.querySelector('.jdgm-rev') || src.querySelector('.jdgm--done-setup-widget'));
  }

  function apply() {
    var src = document.querySelector(SRC);
    if (!src || !widgetReady(src)) return false;

    // Build the real review cards (if any) into our list.
    var list = document.querySelector(LIST);
    var revs = src.querySelectorAll('.jdgm-rev');
    if (list && revs.length && list.getAttribute('data-rendered') !== '1') {
      // Show tidy full rows of 3, capped at 6 (two rows). Judge.me loads ~5 per page, so until its
      // "reviews per page" setting is >=6 this renders 3 rather than a ragged 5; the real aggregate
      // score/count in the header + the Write-a-review button always show the true totals.
      var show = revs.length >= 6 ? 6 : (revs.length >= 3 ? 3 : revs.length);
      var frag = document.createDocumentFragment();
      [].slice.call(revs, 0, show).forEach(function (rev) { frag.appendChild(buildCard(rev)); });
      list.innerHTML = '';
      list.appendChild(frag);
      list.hidden = false;
      list.setAttribute('data-rendered', '1');
    }
    // Reveal the SK design + collapse Judge.me's widget to just its native "Write a review"
    // button (the button MUST stay inside its widget — Judge.me's modal only opens on a real
    // click on its own in-widget button, and moving it out breaks that).
    document.documentElement.classList.add('sk-reviews-active');
    return true;
  }

  function start() {
    if (apply()) return;
    var src = document.querySelector(SRC);
    if (!src) return;
    // Judge.me sets up its widget asynchronously — watch for it, with a polling backstop.
    var obs = new MutationObserver(function () { if (apply()) cleanup(); });
    obs.observe(src, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    var tries = 0;
    var poll = setInterval(function () {
      tries++;
      if (apply() || tries > 40) cleanup(); // ~20s backstop
    }, 500);
    function cleanup() { clearInterval(poll); obs.disconnect(); }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
